import {Component, Vue, Watch} from 'vue-property-decorator';
import {Evaluation} from "@/shared/model/Evaluation";
import {EvaluationType} from "@/shared/model/EvaluationType";
import {ProviderType} from "@/shared/model/ProviderType";
import {ActivityType} from "@/shared/model/ActivityType";
import {County} from "@/shared/model/County";
import {Status} from "@/shared/model/Status";
import {FiscalYear} from "@/shared/model/FiscalYear";
import {Department} from "@/shared/model/Department";
import {RevisitNumber} from "@/shared/model/RevisitNumber";
import {ProviderGroup} from "@/shared/model/ProviderGroup";
import {rules} from "@/shared/model/Rules";
import {evalApi} from "@/plugins/axios";


@Component({
    components: {},
})
export default class EvaluationForm extends Vue {


    //
    // ─── State ────────────────────────────────────────────────
    //
    currentFormStep = 1
    selectedFormType: string | null = null
    evaluation: Evaluation = this.buildDefaultEvaluation()

    // dropdown options
    formTypeOptions = [
        { text: 'Common Project, NAR, HOP and MortSci', value: 'A' },
        { text: 'Assisted Living, Home Care, Funeral Homes, and Body Art Establishments', value: 'B' }//,
        //{ text: 'Option C', value: 'C' }
    ]

    evaluationTypes = EvaluationType.values
    providerTypes = ProviderType.values
    activityTypes = ActivityType.values
    counties = County.values
    surveyTypes: string[] = []
    statuses = Status.values
    fiscalYears = new FiscalYear().values
    revisitNumber = RevisitNumber.values

    // form
    form = { name: '', email: '' }
    requiredRule = rules
    title = 'Evaluation Form'
    maskText = ''
    valid = true
    menu1 = false

    // lookup-related
    providerTypesFormB: string[] = []
    selectedProviderGroupOption: string | null = null

    lookupRefreshKey = 0
    lookupKeyOptions: Array<any> = []
    selectedLookupKey: any = null

    hfidOptions: Array<any> = []
    selectedHfidOption: any = null

    openGovOptions: Array<any> = []
    selectedOpenGovOption: any = null

    providerLookupOptions: Array<any> = []
    selectedProviderOption: any = null


    // UI state
    snackbar = { visible: false, color: 'success', text: '' }
    isLoadingProvider = false
    isLoadingHfid = false
    isReviewLoading = false
    providerModalVisible = false
    evaluationModalVisible = false
    helpDialog = false
    endDateString = ''
    dueDateString = ''

    //
    // ─── Lifecycle ────────────────────────────────────────────
    //
    public created() {
        this.fetchSurveyTypes()
        this.initializeStepOne()
    }


    //
    // ─── Computed ─────────────────────────────────────────────
    //
    get availableDepartmentOptions() {
        return this.selectedFormType === 'B'
            ? Department.valuesFormB
            : Department.values
    }

    get availableEvaluationTypeOptions() {
        return this.selectedFormType === 'B'
            ? EvaluationType.valuesFormB
            : EvaluationType.values
    }

    get availableProviderGroupOptions() {
        return this.selectedFormType === 'B'
            ? ProviderGroup.valuesFormB
            : ProviderGroup.values
    }

    get availableProviderTypeOptions() {
        return this.selectedFormType === 'B'
            ? this.providerTypesFormB
            : ProviderType.values.all
    }

    get computedEndDate(): string {
        return this.formatDateString(this.endDateString)
    }

    get computedDueDate(): string {
        return this.formatDateString(this.dueDateString)
    }

    get evaluationSummaryFields() {
        return [
            ['Evaluation Type', this.evaluation.evaluationType],
            ['Open Gov ID', this.evaluation.openGovId],
            ['HFID', this.evaluation.hfid],
            ['Provider Name', this.evaluation.providerName],
            ['Provider County', this.evaluation.providerCounty],
            ['Provider Type', this.evaluation.providerType],
            ['Provider Group', this.evaluation.providerGroup],
            ['Medicare Number', this.evaluation.medicare],
            ['Department', this.evaluation.department],
            ['Survey Type', this.evaluation.surveyType],
            ['PCR', this.evaluation.revisitNumber],
            ['Fiscal Year', this.evaluation.fiscalYear],
            ['Due Date', this.evaluation.dueDate ? new Date(this.evaluation.dueDate).toLocaleDateString(): null],
            ['Evaluation Number', this.evaluation.evaluationNumber]
        ]
    }



    //
    // ─── Watchers ─────────────────────────────────────────────
    //
    @Watch('selectedLookupKey')
    private onSelectedLookupChanged(newVal: any) {
        if (!newVal) {
            this.resetEvaluationFields();
            return;
        }

        let provider;

        if (newVal.provider) {
            provider = newVal.provider;
        } else {
            const match = this.lookupKeyOptions.find(
                (o) => o.id === newVal || o.id === this.composeProviderId(o.provider)
            );
            provider = match?.provider;
        }

        if (provider) {
            this.selectedHfidOption = provider.hfid;
            this.selectedOpenGovOption = provider.ogRecordNumber;
            this.selectedProviderOption = provider;
            this.applyLookupValues(provider);

            console.log('[onSelectedLookupChanged] provider found:');
            console.log(provider)
        }
    }

    @Watch('selectedHfid')
    private onSelectedHfidChanged(newVal: any) {
        if (!newVal) {
            this.evaluation.hfid = '';
            return;
        }

        if (typeof newVal === 'string') {
            this.evaluation.hfid = newVal;
        } else if (newVal.id) {
            this.evaluation.hfid = newVal.id;
        } else {
            this.evaluation.hfid = this.composeProviderId(newVal);
        }

        console.log('[onSelectedHfidChanged] evaluation.hfid updated:', this.evaluation.hfid);
    }

    @Watch('selectedProvider')
    private onSelectedProviderChanged(newVal: any) {
        if (!newVal) {
            return;
        }

        this.evaluation.providerName = newVal.providerName || '';
    }

    @Watch('evaluation.evaluationType')
    private onEvaluationTypeChanged(newVal: string) {
        if (newVal === 'CHOW' || newVal === 'Initial Inspection') {
            this.evaluation.initialIndicator = 'Y'
        } else {
            this.evaluation.initialIndicator = 'N'
        }
    }

    //
    // ─── Public Methods (template / parent can call) ──────────
    //
    public onFormTypeSelected(val: string) {

        console.log("selectOption", val);

        this.selectedFormType = val;
        this.currentFormStep = 2;

        if (this.selectedFormType === "A"){
            this.evaluation.evaluationNumber = "";
        }


        if (this.selectedFormType === "B" && this.selectedLookupKey) {
            const provider = this.selectedLookupKey.provider ||
                this.lookupKeyOptions.find((o) => o.id === this.selectedLookupKey)?.provider;

            if (provider) {
                this.selectedHfidOption = provider.hfid;
                this.selectedOpenGovOption = provider.ogRecordNumber;
                this.selectedProviderOption = provider;
                this.applyLookupValues(provider);

                //console.log("Restored provider from lookup:", this.selectedHfidOption);
            }
        }
    }

    public async goToNextFormStep() {
        if (this.selectedFormType === 'A') {
            this.currentFormStep++
            this.evaluation.hfid = this.evaluation.providerNumber
            //this.evaluation.evaluationNumber = ''
        } else if (this.selectedFormType === 'B') {
            this.isReviewLoading = true
            this.currentFormStep++
            try {
                await this.fetchEvaluationNumber()
            } finally {
                this.isReviewLoading = false
            }
        }
    }

    public clearStep1() {
        this.initializeStepOne(this.evaluation.evaluationType)
    }

    public async submitNewEvaluation() {
        const res = await evalApi.get(`/evaluations/eval-num-check/` + this.evaluation.evaluationNumber)
        if (res.data) {
            this.snackbar.color = "error"
            this.snackbar.text = "Evaluation '" + this.evaluation.evaluationNumber + "' already exists. No duplicates are allowed."
            this.snackbar.visible = true
            return
        }
        this.evaluation.status = "ACTIVE"
        this.evaluation.jobName = "EvaluationsImport"
        this.evaluation.providerNumber = this.evaluation.hfid ? this.evaluation.hfid : this.evaluation.openGovId
        evalApi.post("/evaluations/save", this.evaluation
        ).then(res => {
            this.snackbar.color = "success"
            this.snackbar.text = "Evaluation '" + this.evaluation.evaluationNumber + "' has been created."
            this.snackbar.visible = true
        }).catch(error => {
            this.snackbar.color = "error"
            this.snackbar.text = "An error occurred submitting the evaluation number: >>>> " + error
            this.snackbar.visible = true
        })
    }

    public async onProviderGroupChange(group: string) {
        this.selectedProviderGroupOption = group;
        this.evaluation.providerGroup = group;
        if (group) {
            console.log("CLEARING >>>>>>");

            this.lookupKeyOptions = [];
            this.hfidOptions = [];
            this.openGovOptions = [];
            this.providerLookupOptions = [];

            this.selectedLookupKey = null;
            this.selectedHfidOption = null;
            this.selectedOpenGovOption = null;
            this.selectedProviderOption = null;

            this.evaluation.hfid = '';
            this.evaluation.providerNumber = null;
            this.evaluation.providerName = null;
            this.evaluation.providerCounty = null;
            this.evaluation.providerType = null;
            this.evaluation.medicare = null;

            this.evaluation.emailAddr =  null;
            this.evaluation.fedId = null;
            this.evaluation.licName = null;
            this.evaluation.licNbr = null;
            this.evaluation.city = null;

            this.lookupRefreshKey++; // force rerender of comboboxes

            (this.$refs.form as any)?.resetValidation();

            await this.fetchLookupOptions(group);
            await this.fetchSurveyTypesB(group);
        }
    }

    //
    // ─── Private Helpers ──────────────────────────────────────
    //
    private buildDefaultEvaluation(): Evaluation {
        const fiscalYearHelper = new FiscalYear();

        return {
            id: null,
            providerNumber: null,
            evaluationNumber: null,
            surveyType: null,
            department: null,
            district: null,
            initialIndicator: null,
            medicare: null,
            insertUid: null,
            insertDate: null,
            aspenEvent: null,
            tierPriority: null,
            chowIndicator: null,
            jiraIssueKey: null,
            evaluationType: null,
            evaluationSubType: null,
            providerName: null,
            providerCounty: null,
            providerType: null,
            fiscalYear: fiscalYearHelper.currentFiscalYear,
            modifyUid: null,
            modifyDate: null,
            jobName: null,
            endDate: null,
            status: null,
            startDate:null,
            dueDate: null,
            revisitNumber: null,
            teamLead: null,
            scheduleStart: null,
            scheduleEnd: null,
            hfid: null,
            providerGroup: null,
            openGovId: null,
            emailAddr: null,
            fedId: null,
            licName: null,
        };
    }

    private initializeStepOne(evaluationType?: string) {
        (this as any).syncingFromHfid = true;
        (this as any).syncingFromProvider = true;

        this.selectedHfidOption = null;
        this.selectedOpenGovOption = null;
        this.selectedProviderOption = null;

        console.log("initStep1")
        Object.assign(this.evaluation, this.buildDefaultEvaluation());

        this.$nextTick(() => {
            this.evaluation.revisitNumber = "0";

            if (evaluationType) {
                this.evaluation.evaluationType = evaluationType;
            }

            (this.$refs.form as any)?.resetValidation();
            (this as any).syncingFromHfid = false;
            (this as any).syncingFromProvider = false;
        });
    }

    private applyLookupValues(selected: any) {
        this.evaluation.providerCounty = (selected?.providerCounty ?? '').toString().trim()
        this.evaluation.hfid = selected.hfid || selected.ogRecordNumber || null;
        this.evaluation.providerName = selected.providerName || null;
        this.evaluation.medicare = selected.medicare || null;
        this.evaluation.providerType = selected.providerTypeCode || null;
        this.evaluation.openGovId = selected.ogRecordNumber || null;
        this.evaluation.emailAddr = selected.emailAddress || null;
        this.evaluation.fedId = selected.fedidOwn || null;
        this.evaluation.licName = selected.licName || null;
        this.evaluation.licNbr = selected.licNbr || null;
        this.evaluation.city = selected.city || null;

        console.log("this.evaluation.hfid>>>>>>>>>>:", this.evaluation.hfid);
        console.log("this.evaluation.medicare>>>>>>>>>>:", this.evaluation.medicare);
        console.log("this.evaluation.providerType>>>>>>>>>>:", this.evaluation.providerType);
    }

    private resetEvaluationFields() {
        this.evaluation.providerCounty = null
        this.evaluation.hfid = null
        this.evaluation.providerName = null
    }

    private composeProviderId(o: any): string {
        return [o.hfid, o.ogRecordNumber, o.providerName].filter(Boolean).join('|')
    }

    private formatDateString(date: string): string {
        if (!date) return 'MM/DD/YYYY'
        const [year, month, day] = date.split('-')
        return `${month}/${day}/${year}`
    }

    //
    // ─── Private API Calls ────────────────────────────────────
    //
    private async fetchSurveyTypes() {
        let flag = null;

        if (this.evaluation.evaluationType === 'Common Project Activities') {
            flag = 'Common Project Activities'
        } else {
            flag = 'Non Common Project Activities'
        }

        evalApi.post('/surveyType/getByFlag', {flag: flag})
            .then((res: any) => {
                this.surveyTypes = res.data;
            })
    }

    private async fetchSurveyTypesB(group: string) {
        try {
            evalApi.post('/surveyType/getByProviderGroup', {providerGroup: group})
                .then((res: any) => {
                    this.surveyTypes = res.data;
                })
        } catch (error) {
            console.error('Failed to load survey type list:', error);
        } finally {
            this.isLoadingProvider = false;
        }

    }

    private async fetchEvaluationNumber() {
        try {
            const response = await evalApi.post<Evaluation>(
                '/project-codes/generate',
                this.evaluation
            );
            this.evaluation.evaluationNumber = response.data.evaluationNumber;
            this.evaluation.jobName = response.data.jobName;
        } catch (error: any) {
            console.error("eeee>>>>", error);

            let message = "Failed to generate evaluation number.";

            if (error.response) {
                const data = error.response.data;

                if (data?.message) {
                    message = data.message;
                } else if (Array.isArray(data?.errors)) {
                    message = data.errors.join(", ");
                } else if (typeof data === "object") {
                    message = Object.values(data).join(", ");
                }
            }


            this.snackbar.color = "error";
            this.snackbar.text = message;
            this.snackbar.visible = true;

            console.error("Error generating evaluation number:", error);
        }
    }

    private async fetchLookupOptions(group: string) {
        try {
            this.isLoadingProvider = true;
            this.isLoadingHfid = true;

            evalApi.get(`/athena/get-by-provider-group/` + group)
                .then(res => {
                    const sortedData = (res.data || []).sort((a: any, b: any) => {
                        const aKey = parseInt(a?.hfid ?? a?.ogRecordNumber ?? '0', 10);
                        const bKey = parseInt(b?.hfid ?? b?.ogRecordNumber ?? '0', 10);
                        return aKey - bKey;
                    });

                    // Lookup options
                    this.lookupKeyOptions = sortedData.map((o: any) => {
                        const parts = [
                            o.ogRecordNumber || '',
                            o.hfid || '',
                            o.providerName || '',
                            o.providerTypeCode || ''
                        ].filter(Boolean)

                        return {
                            id: this.composeProviderId(o),
                            displayText: parts.join(' - '),
                            provider: o
                        }
                    })

                    this.hfidOptions = sortedData.map((o: any) => ({
                        id: this.composeProviderId(o),
                        displayText: [o.hfid, o.providerName]
                            .filter(Boolean)
                            .join(' - ')
                    }));

                    this.openGovOptions = sortedData.map((o: any) => ({
                        id: this.composeProviderId(o),
                        displayText: [o.ogRecordNumber, o.providerName]
                            .filter(Boolean)
                            .join(' - ')
                    }));

                    this.providerLookupOptions = sortedData;
                    this.providerTypesFormB = Array.from<string>(
                        new Set(sortedData.map((o: any) => String(o.providerTypeCode)).filter(Boolean))
                    ).sort();

                    console.log('[providerTypesFormB]', this.providerTypesFormB);
                    const withMedicare = sortedData.filter((o: any) => o.medicare);
                    if (withMedicare.length > 0) {
                        console.log('[Providers with Medicare Number]', withMedicare.map((o: any) => ({
                            name: o.providerName,
                            hfid: o.hfid || o.ogRecordNumber,
                            medicare: o.medicare
                        })));
                    } else {
                        console.log('[Providers with Medicare Number] None found');
                    }
                })
        } catch (error) {
            console.error('Failed to load provider/HFID list:', error);
        } finally {
            this.isLoadingProvider = false;
            this.isLoadingHfid = false;
        }
    }
}